﻿import { useState, useRef, useEffect } from "react";
import { paramsToObject } from "../../../../../Resources/Scripts/_Shared/history";
import { TwoColumnWrapper } from "../../../../../Resources/Scripts/_Shared/elements/ColumnWrapper";
import { DropdownlistWithLabel } from "../../../../../Resources/Scripts/_Shared/elements/DropdownlistWithLabel";
import { CheckboxWithLabel } from "../../../../../Resources/Scripts/_Shared/elements/CheckboxWithLabel";
import { FilterFacilities } from "../utils/facilityFilters.js";
import { getLtpStpKeys } from "../utils/ltpStpKeys";

const AdvFiltersWidget = ({
    facilitySearchContext,
    setFacilitySearchContext,
}) => {
    const lang = {
        ...facilitySearchContext.localization.inputs,
        ...facilitySearchContext.localization.headings,
        ...facilitySearchContext.localization.filter,
    };

    /* All countries want different terminologies for LTP and STP terms
     * And different English translations for these
     * LTP_SWE_alt and STP_SWE_alt translations are used in Sweden (Sweden has Svenska and English as languages)
     * LTP_NOR_alt and STP_NOR_alt translations are used in Norway (Norway has Norsk and English as languages)
     * LTP, STP, LTP_SWE_alt and STP_SWE_alt translations are used in Finland (Finland has Suomi, Svenska and English as languages)
     */
    const country = document.documentElement
        ?.getAttribute("country")
        ?.toLowerCase();

    const { LTP_KEY, STP_KEY } = getLtpStpKeys(country, lang.info);

    const LTP_TEXT = lang.info[LTP_KEY] || lang.info.LTP || "LTP";
    const STP_TEXT = lang.info[STP_KEY] || lang.info.STP || "STP";

    const [filterSelection, setFilterSelection] = useState(
        facilitySearchContext.filterSelection,
    );
    const [currentServiceFilters, setCurrentServiceFilters] = useState([]);
    const [currentServiceFiltersOptions, setCurrentServiceFiltersOptions] =
        useState([]);
    const [currentParkingSpaceTypes, setCurrentParkingSpaceTypes] = useState(
        [],
    );
    const [
        currentParkingSpaceTypesOptions,
        setCurrentParkingSpaceTypesOptions,
    ] = useState([]);
    const [filterSelected, setFilterSelected] = useState(false);
    const [bookingTypes, setBookingTypes] = useState(null);
    const [currentBookingType, setCurrentBookingType] = useState(null);
    const containerRef = useRef(null);
    const emptyGuid = "00000000-0000-0000-0000-000000000000";
    const selectValue = {
        item: { Text: "Välj", Id: emptyGuid, initial: true },
    };

    useEffect(() => {
        if (
            facilitySearchContext.allFacilities &&
            (filterSelected || facilitySearchContext.init)
        ) {
            const searchHits = FilterFacilities(
                facilitySearchContext.allFacilities,
                filterSelection,
            );
            const delay = facilitySearchContext.init ? 500 : 0;
            setTimeout(() => {
                applyFiltersHandler(searchHits);
            }, delay);
        }
        handleScroll();
    }, [filterSelection]);

    useEffect(() => {
        if (
            !facilitySearchContext.allFacilities ||
            facilitySearchContext.allFacilities.length === 0
        ) {
            const activeFilters = checkActiveFilters();
            const activeParkingFilters = checkActiveFilters(true);

            const tempFilterOptions = [];
            for (const [key] of Object.entries(filterSelection)) {
                const skip =
                    key === "ParkingSpaceTypeSelection" ||
                    key === "LTP" ||
                    key === "STP" ||
                    (filterSelection.LTP &&
                        (key === "disabled" ||
                            key === "electricChargingPoint"));
                if (!skip && activeFilters.includes(key))
                    tempFilterOptions.push({
                        item: {
                            Id: key,
                            value: key,
                            Text: (
                                lang.services[key.toLowerCase()] ||
                                lang.info[
                                    key.charAt(0).toLowerCase() + key.slice(1)
                                ] ||
                                key
                            ).replace("&shy;", ""),
                        },
                    });
            }
            const tempParkingOptions = activeParkingFilters.map((type) => ({
                item: { Id: type, value: type, Text: type },
            }));

            setCurrentServiceFilters(activeFilters);
            setCurrentParkingSpaceTypesOptions(tempParkingOptions);
            setCurrentServiceFiltersOptions(tempFilterOptions);
            setBookingTypes([
                {
                    value: "LTP",
                    id: "LTP",
                    Text: lang.info[LTP_KEY],
                    disabled: true,
                },
                {
                    value: "STP",
                    id: "STP",
                    Text: lang.info[STP_KEY],
                    disabled: true,
                },
            ]);
            return;
        }

        const uniqueServices = getUniqueServices();
        const parkingSpaceTypeSelection = [];

        for (const [key] of Object.entries(
            filterSelection.ParkingSpaceTypeSelection,
        )) {
            filterSelection.ParkingSpaceTypeSelection[key] &&
                parkingSpaceTypeSelection.push(key);
        }
        const [parkingSpaceTypes, parkingSpaceTypesAsObject] =
            setProductOptions();

        setFilterSelected(false);
        setCurrentServiceFilters(uniqueServices);
        setCurrentParkingSpaceTypes(
            Array.from(
                new Set([
                    ...parkingSpaceTypeSelection,
                    ...parkingSpaceTypes.flat(),
                ]),
            ),
        );
    }, [facilitySearchContext.allFacilities]);

    useEffect(() => {
        if (currentParkingSpaceTypes)
            setCurrentParkingSpaceTypesOptions(
                Array.from(new Set(currentParkingSpaceTypes)).map((type) => ({
                    item: { Id: type, value: type, Text: type },
                })),
            );

        setCurrentBookingType({
            item: filterSelection.STP
                ? {
                      value: "STP",
                      Id: "STP",
                      Text: lang.info[STP_KEY],
                  }
                : {
                      value: "LTP",
                      Id: "LTP",
                      Text: lang.info[LTP_KEY],
                  },
        });
        const tempBookingTypes = [
            {
                value: "LTP",
                id: "LTP",
                Text: lang.info[LTP_KEY],
                disabled: !currentServiceFilters.includes("LTP"),
            },
            {
                value: "STP",
                Id: "STP",
                Text: lang.info[STP_KEY],
                disabled: !currentServiceFilters.includes("STP"),
            },
        ];
        setBookingTypes(tempBookingTypes);
        const tempOptions = [];

        for (const [key] of Object.entries(filterSelection)) {
            const skip =
                key === "ParkingSpaceTypeSelection" ||
                key === "LTP" ||
                key === "STP";
            if (!skip)
                tempOptions.push({
                    item: {
                        Id: key,
                        value: key,
                        Text: (
                            lang.services[key.toLowerCase()] ||
                            lang.info[
                                key.charAt(0).toLowerCase() + key.slice(1)
                            ] ||
                            key
                        ).replace("&shy;", " "),
                    },
                });
        }
        setCurrentServiceFiltersOptions(
            Array.from(new Set(tempOptions.map((o) => o.item.value))).map(
                (val) => ({
                    item: tempOptions.find((o) => o.item.value === val).item,
                }),
            ),
        );
    }, [currentParkingSpaceTypes, filterSelection, currentServiceFilters]);

    const checkActiveFilters = (products, filterName, filters) => {
        const activeFilters = [];
        const tempFilterSelection = filters ? filters : { ...filterSelection };
        if (products)
            for (const [key] of Object.entries(
                tempFilterSelection.ParkingSpaceTypeSelection,
            )) {
                if (
                    tempFilterSelection.ParkingSpaceTypeSelection[key] &&
                    (!filterName || key !== filterName)
                )
                    activeFilters.push(key);
            }
        else
            for (const [key] of Object.entries(tempFilterSelection)) {
                const skip =
                    key === "ParkingSpaceTypeSelection" ||
                    (filterSelection.LTP &&
                        (key === "disabled" ||
                            key === "electricChargingPoint"));
                if (
                    tempFilterSelection[key] &&
                    !skip &&
                    (!filterName || key !== filterName)
                )
                    activeFilters.push(key);
            }
        return activeFilters;
    };

    const applyFiltersHandler = (searchHits) => {
        const urlParams = new URLSearchParams(window.location.search);
        urlParams.delete("f");
        urlParams.delete("p");
        const filters = checkActiveFilters(false, null, filterSelection);
        const parkingFilters = checkActiveFilters(true, null, filterSelection);
        const updateMapBounds = !!(
            getNumberOfFilters(filterSelection) - 1 ||
            getNumberOfFilters(filterSelection.ParkingSpaceTypeSelection) ||
            !facilitySearchContext.init
        );

        if (filters.length) urlParams.set("f", filters.join(","));

        if (parkingFilters.length) urlParams.set("p", parkingFilters.join(","));

        const newUrl =
            location.origin +
            window.location.pathname +
            "?" +
            urlParams.toString();
        history.pushState(paramsToObject(urlParams), document.title, newUrl);
        if (searchHits.length)
            setFacilitySearchContext({
                ...facilitySearchContext,
                setListEmpty: false,
                filtersOpen: false,
                filterSelection: { ...filterSelection },
                visibleFacilities: searchHits,
                filteredFacilities: searchHits,
                updateMapBounds,
                updateMarkers: true,
                init: false,
            });
        else
            setFacilitySearchContext({
                ...facilitySearchContext,
                setListEmpty: true,
                filterSelection,
                filteredFacilities: searchHits,
                visibleFacilities: [],
                updateMapBounds: false,
                updateMarkers: true,
                init: true,
                showLoadingScreen: false,
            });
    };

    const setProductOptions = () => {
        const parkingSpaceTypes = [];
        const parkingSpaceTypesAsObject = {};
        for (let i = 0; i < facilitySearchContext.allFacilities.length; i++) {
            const singleFacility = facilitySearchContext.allFacilities[i];
            let singleParkingSpaceTypes = singleFacility.ParkingZones.map(
                (zone) =>
                    zone.Pmcs && zone.Pmcs.map((pmc) => pmc.ParkingSpaceType),
            )[0];
            singleParkingSpaceTypes = singleParkingSpaceTypes
                ? singleParkingSpaceTypes.filter(Boolean)
                : [];
            if (singleParkingSpaceTypes)
                parkingSpaceTypes.push(...singleParkingSpaceTypes);
        }
        if (parkingSpaceTypes && parkingSpaceTypes.length)
            Array.from(
                new Set(parkingSpaceTypes.filter(Boolean).flat()),
            ).forEach(
                (type) =>
                    (parkingSpaceTypesAsObject[type] = filterSelection
                        .ParkingSpaceTypeSelection[type]
                        ? filterSelection.ParkingSpaceTypeSelection[type]
                        : false),
            );

        return [parkingSpaceTypes, parkingSpaceTypesAsObject];
    };

    const getUniqueServices = () => {
        const uniqueServices = [];
        for (let i = 0; i < facilitySearchContext.allFacilities.length; i++) {
            const singleFacility = facilitySearchContext.allFacilities[i];
            const facilityServices = singleFacility.Services;
            //singleParkingSpaceTypes = singleParkingSpaceTypes.length > 0 && Array.from(new Set(singleParkingSpaceTypes));

            const facilityInfo = {
                Indoor: singleFacility.Indoor,
                Outdoor: !singleFacility.Indoor,
                Heated:
                    singleFacility.Heated ??
                    singleFacility.Info?.Heated ??
                    false,
                MaxVehicleHeight:
                    (singleFacility.MaxVehicleHeight ??
                        singleFacility.Info?.MaxVehicleHeight ??
                        0) > 200,
                LTP: singleFacility.HasLtp,
                STP: singleFacility.HasStp,
                ...singleFacility.Info,
            };

            if (facilityInfo.LTP || facilityInfo.STP) {
                for (let x = 0; x < facilityServices.length; x++) {
                    var singleService = facilityServices[x];
                    if (
                        uniqueServices.filter(
                            (service) => service === singleService,
                        ).length === 0
                    )
                        uniqueServices.push(singleService);
                }

                for (const [key] of Object.entries(facilityInfo)) {
                    if (
                        facilityInfo[key] &&
                        key !== "type" &&
                        !uniqueServices.includes(key)
                    )
                        uniqueServices.push(key);
                }
            }
        }
        return uniqueServices;
    };

    const removeServiceItem = (e) => {
        setFilterSelected(true);
        const filterName = e.currentTarget.dataset.value;
        e.currentTarget.closest("li").classList.add("remove");
        const activeFilters = checkActiveFilters(false, filterName);
        const tempOptions = [];
        const tempFilterSelection = { ...filterSelection, [filterName]: false };
        const tempServiceFilters = Array.from(
            new Set([...getUniqueServices(), ...activeFilters]),
        );
        for (const [key] of Object.entries(tempFilterSelection)) {
            const skip =
                key === "ParkingSpaceTypeSelection" ||
                key === "LTP" ||
                key === "STP" ||
                (filterSelection.LTP &&
                    (key === "disabled" || key === "electricChargingPoint"));
            if (
                !skip &&
                (tempServiceFilters.includes(key) || tempFilterSelection[key])
            )
                tempOptions.push({
                    item: {
                        Id: key,
                        value: key,
                        Text: (
                            lang.services[key.toLowerCase()] ||
                            lang.info[
                                key.charAt(0).toLowerCase() + key.slice(1)
                            ] ||
                            key
                        ).replace("&shy;", ""),
                    },
                });
        }
        setTimeout(() => {
            setFilterSelection({ ...filterSelection, [filterName]: false });
            setCurrentServiceFilters([
                ...getUniqueServices(),
                ...activeFilters,
            ]);
            setCurrentServiceFiltersOptions(tempOptions);
        }, 200);
    };

    const removeParkingspaceTypeItem = (e) => {
        setFilterSelected(true);
        const filterName = e.currentTarget.dataset.value;
        e.currentTarget.closest("li").classList.add("remove");
        const [parkingSpaceTypes, parkingSpaceTypesAsObject] =
            setProductOptions();
        const activeFilters = checkActiveFilters(true, filterName);
        const dublicatesRemoved = Array.from(
            new Set([...parkingSpaceTypes, ...activeFilters]),
        );
        const tempOptions = dublicatesRemoved.map((type) => ({
            item: { Id: type, value: type, Text: type },
        }));
        setTimeout(() => {
            setFilterSelection({
                ...filterSelection,
                ParkingSpaceTypeSelection: {
                    ...filterSelection.ParkingSpaceTypeSelection,
                    [filterName]: false,
                },
            });
            setCurrentParkingSpaceTypesOptions(
                filterSelection.STP ? [] : tempOptions,
            );
        }, 200);
    };

    const overflowLeftOrRight = () => {
        const edges = ["left", "right"];
        if (containerRef.current) {
            const { current } = containerRef;
            if (current.scrollLeft <= 5) edges[0] = null;
            if (
                current.offsetWidth !== 0 &&
                Math.abs(
                    current.scrollWidth -
                        current.clientWidth -
                        current.scrollLeft,
                ) <= 5
            )
                edges[1] = null;
            if (
                current.offsetWidth !== 0 &&
                current.offsetWidth >= current.scrollWidth
            )
                edges[0] = edges[1] = null;
        }
        return edges;
    };

    const handleScroll = () => {
        const wrapper = containerRef.current.parentNode;
        const edges = overflowLeftOrRight();
        wrapper
            .querySelectorAll(".shadow")
            .forEach((shadow) => shadow.classList.remove("visible"));
        edges.forEach((edge) => {
            if (edge)
                wrapper
                    .querySelector(`.shadow--${edge}`)
                    .classList.add("visible");
        });
    };

    const getNumberOfFilters = (object) => {
        return Object.values(object).reduce(
            (a, item) => a + (item === true ? 1 : 0),
            0,
        );
    };

    const getUniqueDropdownOptions = (serviceOptions, parkingOptions) => {
        const uniqueValues = new Set([
            ...serviceOptions.map((o) => o.item.value),
            ...parkingOptions.map((o) => o.item.value),
        ]);
        return Array.from(uniqueValues).map((val) => {
            return (
                serviceOptions.find((o) => o.item.value === val) ||
                parkingOptions.find((o) => o.item.value === val)
            );
        });
    };
    const unifiedFilterOptions = getUniqueDropdownOptions(
        currentServiceFiltersOptions,
        currentParkingSpaceTypesOptions,
    );

    const activeCount =
        getNumberOfFilters(filterSelection.ParkingSpaceTypeSelection) +
        Object.entries(filterSelection).filter(
            ([key, value]) =>
                value === true &&
                !["LTP", "STP", "ParkingSpaceTypeSelection"].includes(key),
        ).length;

    return (
        <>
            <TwoColumnWrapper>
                <TwoColumnWrapper>
                    {/* Use ckecboxes buttons (CPS-24701) */}
                    <CheckboxWithLabel
                        text={LTP_TEXT}
                        value={filterSelection.LTP}
                        setValue={(checked) => {
                            const newSelection = {
                                ...filterSelection,
                                LTP: checked,
                            };

                            setFilterSelection(newSelection);
                            setFilterSelected(true);
                        }}
                    />
                    <CheckboxWithLabel
                        text={STP_TEXT}
                        value={filterSelection.STP}
                        setValue={(checked) => {
                            const newSelection = {
                                ...filterSelection,
                                STP: checked,
                            };

                            setFilterSelection(newSelection);
                            setFilterSelected(true);
                        }}
                    />
                </TwoColumnWrapper>
                {/* Unified dropdown list for both LTP and STP (CPS-24701) */}
                <DropdownlistWithLabel
                    text={lang.filter}
                    value={{
                        item: {
                            ...selectValue.item,
                            Text: `${
                                activeCount > 0 ? "(" + activeCount + ")" : ""
                            } ${lang.select}`,
                        },
                    }}
                    options={unifiedFilterOptions}
                    setValue={(option) => {
                        const value = option.item.item.value;

                        setFilterSelection((prev) => {
                            const newSelection = { ...prev };
                            if (
                                prev.ParkingSpaceTypeSelection?.hasOwnProperty(
                                    value,
                                )
                            ) {
                                newSelection.ParkingSpaceTypeSelection = {
                                    ...prev.ParkingSpaceTypeSelection,
                                    [value]:
                                        !prev.ParkingSpaceTypeSelection[value],
                                };
                            } else {
                                newSelection[value] = !prev[value];
                            }
                            return newSelection;
                        });
                        setFilterSelected(true);
                    }}
                />
            </TwoColumnWrapper>
            <div className="facilitysearchpage__advfilters__filterlist__wrapper">
                <span className="shadow shadow--left"></span>
                <span className="shadow shadow--right"></span>
                <ul
                    ref={containerRef}
                    onScroll={handleScroll}
                    className="facilitysearchpage__advfilters__filterlist"
                >
                    {currentServiceFiltersOptions.length
                        ? currentServiceFiltersOptions.map((filter) => {
                              if (
                                  filter.item.value === "STP" ||
                                  filter.item.value === "LTP"
                              )
                                  return null;
                              return (
                                  filterSelection[filter.item.value] && (
                                      <li key={filter.item.value}>
                                          <span>{filter.item.Text}</span>
                                          <div
                                              className="close"
                                              onClick={removeServiceItem}
                                              data-value={filter.item.value}
                                          >
                                              <span></span>
                                              <span></span>
                                          </div>
                                      </li>
                                  )
                              );
                          })
                        : null}
                    {currentParkingSpaceTypesOptions.length
                        ? currentParkingSpaceTypesOptions.map((filter) => {
                              return (
                                  filterSelection.ParkingSpaceTypeSelection[
                                      filter.item.value
                                  ] && (
                                      <li key={filter.item.value}>
                                          <span>{filter.item.Text}</span>
                                          <div
                                              className="close"
                                              onClick={
                                                  removeParkingspaceTypeItem
                                              }
                                              data-value={filter.item.value}
                                              data-product={true}
                                          >
                                              <span></span>
                                              <span></span>
                                          </div>
                                      </li>
                                  )
                              );
                          })
                        : null}
                </ul>
            </div>
        </>
    );
};

export default AdvFiltersWidget;
